import { useState } from "react";
import { NodoHipotesis, ARBOL_HIPOTESIS, getDocumentosRecomendados } from "../../data/arbolHipotesis";

interface ArbolHipotesisSelectorProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

function NodoItem({
  nodo,
  selectedIds,
  onToggle,
  depth,
}: {
  nodo: NodoHipotesis;
  selectedIds: string[];
  onToggle: (id: string) => void;
  depth: number;
}) {
  const [expanded, setExpanded] = useState(depth < 1);
  const hasChildren = nodo.children && nodo.children.length > 0;
  const isLeaf = !hasChildren;
  const isSelected = selectedIds.includes(nodo.id);

  // Count selected descendants
  const countSelected = (n: NodoHipotesis): number => {
    if (!n.children) return selectedIds.includes(n.id) ? 1 : 0;
    return n.children.reduce((sum, child) => sum + countSelected(child), 0);
  };
  const selectedCount = hasChildren ? countSelected(nodo) : 0;

  return (
    <div>
      <div
        className={`flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer hover:bg-slate-50 ${
          isSelected ? "bg-blue-50" : ""
        }`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => {
          if (isLeaf) {
            onToggle(nodo.id);
          } else {
            setExpanded(!expanded);
          }
        }}
      >
        {hasChildren && (
          <svg
            className={`w-3.5 h-3.5 text-slate-400 transition-transform flex-shrink-0 ${
              expanded ? "rotate-90" : ""
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        )}
        {isLeaf && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggle(nodo.id)}
            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <span className={`text-sm ${isLeaf ? "text-slate-700" : "font-medium text-slate-800"}`}>
          {nodo.label}
        </span>
        {selectedCount > 0 && (
          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full ml-auto">
            {selectedCount}
          </span>
        )}
      </div>
      {expanded && hasChildren && (
        <div>
          {nodo.children!.map((child) => (
            <NodoItem
              key={child.id}
              nodo={child}
              selectedIds={selectedIds}
              onToggle={onToggle}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ArbolHipotesisSelector({
  selectedIds,
  onChange,
}: ArbolHipotesisSelectorProps) {
  const handleToggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((s) => s !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const docsRecomendados = getDocumentosRecomendados(selectedIds);

  return (
    <div className="space-y-3">
      <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto">
        {ARBOL_HIPOTESIS.map((nodo) => (
          <NodoItem
            key={nodo.id}
            nodo={nodo}
            selectedIds={selectedIds}
            onToggle={handleToggle}
            depth={0}
          />
        ))}
      </div>

      {selectedIds.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-600 mb-1">
            Hipotesis seleccionadas ({selectedIds.length}):
          </p>
          <div className="flex flex-wrap gap-1">
            {selectedIds.map((id) => (
              <span
                key={id}
                className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200 cursor-pointer hover:bg-blue-100"
                onClick={() => handleToggle(id)}
                title="Click para remover"
              >
                {id.split(".").pop()?.replace(/_/g, " ")} x
              </span>
            ))}
          </div>
        </div>
      )}

      {docsRecomendados.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
          <p className="text-xs font-medium text-amber-700 mb-1">
            Documentos recomendados:
          </p>
          <ul className="text-xs text-amber-600 space-y-0.5">
            {docsRecomendados.map((doc, i) => (
              <li key={i}>- {doc}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
